import React from 'react';
import { Building2, User, Briefcase, Crown, Share2, Users, Edit, Trash2 } from 'lucide-react';

export interface Relation {
  id: string;
  parent_entity_id: string;
  child_entity_id: string;
  relation_kind: 'shareholder' | 'executive' | 'director' | 'ubo' | 'other';
  percentage?: number;
  entity_type: 'company' | 'person';
  entity_name: string;
  created_at: string;
  updated_at: string;
}

interface RelationCardProps {
  relation: Relation;
  direction: 'incoming' | 'outgoing';
  onEntityClick: (entityId: string) => void;
  onEdit: (relation: Relation) => void;
  onDelete: (relation: Relation) => void;
  isDeleting?: boolean;
}

const getRelationKindLabel = (kind: string) => {
  const labels: Record<string, string> = {
    director: 'Directeur',
    executive: 'Dirigeant',
    shareholder: 'Actionnaire',
    ubo: 'UBO',
    other: 'Autre',
  };
  return labels[kind] || kind;
};

const getRelationKindIcon = (kind: string) => {
  switch (kind) {
    case 'director':
      return Briefcase;
    case 'executive':
      return Crown;
    case 'shareholder':
      return Share2;
    case 'ubo':
      return Users;
    default:
      return User;
  }
};

const getRelationKindColor = (kind: string) => {
  switch (kind) {
    case 'director':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'executive':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'shareholder':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'ubo':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const RelationCard: React.FC<RelationCardProps> = ({ 
  relation, 
  direction, 
  onEntityClick, 
  onEdit, 
  onDelete,
  isDeleting = false
}) => {
  const Icon = getRelationKindIcon(relation.relation_kind);
  const EntityIcon = relation.entity_type === 'company' ? Building2 : User;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getRelationKindColor(relation.relation_kind)}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <button
                onClick={() => onEntityClick(direction === 'incoming' ? relation.parent_entity_id : relation.child_entity_id)}
                className="font-semibold text-gray-900 hover:text-blue-600 transition-colors text-left truncate"
              >
                {relation.entity_name}
              </button>
              <EntityIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </div>
            <div className="flex items-center space-x-2 flex-wrap">
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRelationKindColor(relation.relation_kind)}`}>
                {getRelationKindLabel(relation.relation_kind)}
              </span>
              {relation.percentage !== undefined && relation.percentage !== null && (
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                  {relation.percentage}%
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => onEdit(relation)}
            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
            title="Modifier"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(relation)}
            disabled={isDeleting}
            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
            title="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

